import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonLink } from '@resideo/blueprint-oneportal';
import styles from './InviteEmployeeStepReview.module.css';
import type { LocationRow } from './InviteEmployeeStepLocation';
import { CameraAccessModal } from './CameraAccessModal';
import type { CameraDevice } from './CameraAccessModal';

// ─── Camera icon (from GXD cameras_icon.svg) ─────────────────────────────────

function CameraIconGXD() {
  return (
    <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
      <path
        d="M19.5418 14H23.7792C23.978 14.0001 24.1734 14.051 24.347 14.1479C24.5206 14.2447 24.6666 14.3843 24.7711 14.5535C24.8756 14.7226 24.9351 14.9156 24.944 15.1142C24.953 15.3128 24.911 15.5103 24.8222 15.6882L22.4492 20.4353C22.3592 20.6152 22.2242 20.7688 22.0574 20.8811C21.8906 20.9935 21.6975 21.0608 21.497 21.0765C21.2965 21.0922 21.0953 21.0559 20.913 20.9709C20.7307 20.886 20.5734 20.7553 20.4565 20.5917L17.9785 17.1267"
        fill="#DBE1F0"
        stroke="#60646C"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.9574 10.5616C20.2339 10.7 20.4441 10.9426 20.5419 11.2359C20.6397 11.5293 20.617 11.8495 20.4789 12.1261L16.8552 19.3723C16.7867 19.5093 16.6918 19.6316 16.576 19.732C16.4602 19.8324 16.3258 19.909 16.1804 19.9574C16.035 20.0058 15.8815 20.0251 15.7287 20.0142C15.5758 20.0033 15.4266 19.9624 15.2895 19.8938L4.21205 14.3498C3.40744 13.9445 2.79613 13.237 2.51189 12.3821C2.22765 11.5272 2.29362 10.5945 2.69538 9.78811L4.30538 6.53311C4.50605 6.1332 4.78353 5.77674 5.12197 5.48409C5.46042 5.19143 5.8532 4.96831 6.27789 4.82747C6.70257 4.68663 7.15084 4.63083 7.5971 4.66325C8.04335 4.69568 8.47885 4.81569 8.87871 5.01645L19.9574 10.5616Z"
        stroke="#60646C"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.33301 22.1667H6.71968C7.15453 22.1697 7.58157 22.0511 7.95263 21.8244C8.32369 21.5976 8.624 21.2717 8.81967 20.8833L10.4997 17.5"
        stroke="#60646C"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.33301 24.4997V19.833"
        stroke="#60646C"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.16699 10.5H8.17866"
        stroke="#60646C"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface StepReviewProps {
  firstName?: string;
  lastName?: string;
  email: string;
  roleLabel: string;
  isAdmin?: boolean;
  locations: LocationRow[];
  selectedLocationIds: Set<string>;
  cameraSelections: Map<string, Set<string>>;
  onCameraSelectionsChange?: (next: Map<string, Set<string>>) => void;
  onEditLocations?: () => void;
  onEditUser?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function StepReview({
  firstName,
  lastName,
  email,
  roleLabel,
  isAdmin = false,
  locations,
  selectedLocationIds,
  cameraSelections,
  onCameraSelectionsChange,
  onEditLocations,
  onEditUser,
}: StepReviewProps) {
  const { t } = useTranslation();
  const selectedLocations = locations.filter(l => selectedLocationIds.has(l.id));

  const [accessModal, setAccessModal] = useState<{
    locationId: string;
    locationName: string;
    devices: CameraDevice[];
    selected: Set<string>;
  } | null>(null);

  return (
    <div className={styles.reviewContainer}>
      {/* Title */}
      <h2 className={styles.reviewTitle}>Review</h2>
      <p className={styles.reviewSubtitle}>{t('inviteEmployee.stepReviewSubtitle')}</p>

      {/* ── Employee section ────────────────────────────────────────────────── */}
      <div className={styles.sectionHeadingRow}>
        <span className={styles.sectionHeading}>{t('inviteEmployee.userInformation')}</span>
        {onEditUser && (
          <button type="button" className={styles.editLink} onClick={onEditUser}>
            {t('inviteEmployee.edit')}
          </button>
        )}
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.fieldsGrid}>
          {(firstName || lastName) && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('inviteEmployee.nameLabel').toUpperCase()}</span>
              <span className={styles.fieldValue}>{[firstName, lastName].filter(Boolean).join(' ') || '—'}</span>
            </div>
          )}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('inviteEmployee.emailLabel').toUpperCase()}</span>
            <span className={styles.fieldValue}>{email || '—'}</span>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('inviteEmployee.roleLabel').toUpperCase()}</span>
            <span className={styles.fieldValue}>{roleLabel || '—'}</span>
          </div>
        </div>
      </div>

      {/* ── Locations section ──────────────────────────────────────────────── */}
      <div className={styles.locationsHeader}>
        <div className={styles.sectionHeadingRow}>
          <span className={styles.sectionHeading}>
            Locations ({selectedLocations.length})
          </span>
          {onEditLocations && (
            <button type="button" className={styles.editLink} onClick={onEditLocations}>
              {t('inviteEmployee.edit')}
            </button>
          )}
        </div>
      </div>

      {selectedLocations.length === 0 && (
        <div className={styles.emptyLocations}>No locations selected.</div>
      )}

      {selectedLocations.map(loc => {
        const cameraTotal = loc.devices.length;
        const cameraSelectedCount = (cameraSelections.get(loc.id) ?? new Set()).size;

        return (
          <div key={loc.id} className={styles.locationCard}>
            {/* Location name + address */}
            <div className={styles.locationCardHeader}>
              <span className={styles.locationName}>{loc.name}</span>
              {loc.fullAddress && (
                <span className={styles.locationAddress}>{loc.fullAddress}</span>
              )}
            </div>

            {/* Cameras device row */}
            <div className={styles.devicesSection}>
            <div className={styles.devicesBox}>
            <div className={styles.deviceRow}>
              <div className={styles.deviceLeft}>
                <span className={styles.deviceIcon}>
                  <CameraIconGXD />
                </span>
                <span className={styles.deviceLabel}>Cameras</span>
                <span className={styles.deviceCount}>
                  {cameraSelectedCount} of {cameraTotal} cameras
                </span>
              </div>
              <ButtonLink
                className={styles.viewAccessLink}
                onClick={() =>
                  setAccessModal({
                    locationId: loc.id,
                    locationName: loc.name,
                    devices: loc.devices,
                    selected: cameraSelections.get(loc.id) ?? new Set(),
                  })
                }
              >
                {isAdmin ? 'View Access' : 'Edit Access'}
              </ButtonLink>
            </div> 
            </div>
            </div>
          </div>
        );
      })}

      {/* Camera Access modal — read-only for ADMIN, editable for others */}
      {accessModal && (
        <CameraAccessModal
          locationName={accessModal.locationName}
          devices={accessModal.devices}
          initialSelected={accessModal.selected}
          onSave={isAdmin ? undefined : selected => {
            if (onCameraSelectionsChange) {
              const next = new Map(cameraSelections);
              next.set(accessModal.locationId, selected);
              onCameraSelectionsChange(next);
            }
            setAccessModal(null);
          }}
          onCancel={() => setAccessModal(null)}
          readOnly={isAdmin}
        />
      )}
    </div>
  );
}
